/**
 * Print utility functions for opening browser print dialog
 */

/**
 * Open browser print dialog for images
 * @param {Array} images - Array of image objects with file_name property
 * @param {string} documentName - Name of the document for the print
 * @returns {Promise<void>}
 */
export async function printImages(images, documentName = 'Document') {
  try {
    // Build print content (no whitespace, no overlay)
    let printContent = ''
    for (let i = 0; i < images.length; i++) {
      printContent += `<div class="print-page"><img src="${images[i].file_name}" class="print-image"/></div>`
    }

    // Print-specific styles
    const printStyles = `
      <style>
        @media print {
          html, body {
            width: 210mm !important;
            height: 297mm !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
          }
          .print-page {
            width: 210mm !important;
            height: 297mm !important;
            page-break-after: always !important;
            display: flex !important;
            align-items: center;
            justify-content: center;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }
          .print-page:last-of-type { page-break-after: avoid !important; }
          .print-image {
            max-width: 100% !important;
            max-height: 100% !important;
            width: auto !important;
            height: auto !important;
            object-fit: contain !important;
            display: block !important;
            margin: auto !important;
            border: none !important;
            box-sizing: border-box !important;
          }
          @page { size: A4 portrait; margin: 0; }
        }
      </style>
    `

    // Open a new window for printing
    const printWindow = window.open('', '_blank')
    if (!printWindow) throw new Error('Failed to open print window')

    printWindow.document.write(`
      <html>
        <head>
          <title>${documentName}</title>
          ${printStyles}
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `)

    // Wait for all images to load before printing
    const waitForImages = () => {
      return new Promise((resolve) => {
        const imgs = printWindow.document.querySelectorAll('img')
        let loaded = 0
        if (imgs.length === 0) resolve()
        imgs.forEach((img) => {
          if (img.complete) {
            loaded++
            if (loaded === imgs.length) resolve()
          } else {
            img.onload = img.onerror = () => {
              loaded++
              if (loaded === imgs.length) resolve()
            }
          }
        })
      })
    }

    printWindow.document.close()
    await new Promise((r) => setTimeout(r, 100)) // Let DOM render
    await waitForImages()

    printWindow.focus()
    printWindow.print()
    printWindow.close()
  } catch (error) {
    throw new Error('Failed to open print dialog: ' + error.message)
  }
}

/**
 * Print a single document group (all images in that document)
 * @param {Object} documentGroup - Document group object with data array
 * @returns {Promise<void>}
 */
export async function printDocumentGroup(documentGroup) {
  if (!documentGroup || !documentGroup.data || documentGroup.data.length === 0) {
    throw new Error('No images found in document group')
  }

  const documentName = documentGroup.document_name_full || documentGroup.document_name || 'Document'
  await printImages(documentGroup.data, documentName)
}

/**
 * Print all documents in the job
 * @param {Array} documentGroups - Array of document group objects
 * @returns {Promise<void>}
 */
export async function printAllDocuments(documentGroups) {
  if (!documentGroups || documentGroups.length === 0) {
    throw new Error('No documents found')
  }

  // Flatten all images from all document groups
  const allImages = []
  documentGroups.forEach((group, groupIndex) => {
    const documentName = group.document_name_full || group.document_name || `Document ${groupIndex + 1}`
    group.data.forEach((image, imageIndex) => {
      allImages.push({
        ...image,
        documentName: documentName,
        groupIndex: groupIndex,
        imageIndex: imageIndex,
      })
    })
  })

  await printImages(allImages, 'All Documents')
}
